import React, {useEffect, useMemo, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'
import type {ApexOptions} from 'apexcharts'
import {KTIcon} from '../../../_metronic/helpers'
import bm from './businessMatrix.module.css'
import {formatDateToYearMonth} from '../../common-function/formatDateToYearMonth'
import {restRequest} from '../../../api-utils/apiQueries'

type MonthlyRevenueRow = {
  month: string
  subscription: number
  passenger_credit: number
}
type MauDauRow = {
  month: string
  mau_driver: number
  mau_passenger: number
  dau_driver: number
  dau_passenger: number
}
type RetentionMonthlyRow = {
  month: string
  driver_retention: number
  passenger_retention: number
}

type BusinessMatrixData = {
  currency: string
  month: string
  year: number
  revenue: {
    total: number
    subscription: number
    passenger_credit: number
    mom_change: {subscription: number; passenger_credit: number}
    monthly_breakdown: MonthlyRevenueRow[]
    chart_y_axis_max: number
    selected_month_index: number
  }
  points: {
    issued: number
    redeemed: number
    not_redeemed: number
    redeemed_in_month: number
    in_passenger_wallets: number
    accumulated_driver_balances: number
  }
  rates: {
    churn_rate: number
    churn_formula: string
    breakage_rate: number
    breakage_formula: string
    retention_rate: number | null
    retention_formula: string
    growth_rate: number
    growth_formula: string
  }
  subscribers: {
    start_of_month: number
    new_driver_subscriptions: number
    churned: number
    end_of_month: number
    mom_change: {
      start_of_month: number
      new_driver_subscriptions: number
      churned: number
      end_of_month: number
    }
  }
  engagement: {mau_dau_monthly: MauDauRow[]}
  passengers: {total_registered: number; active_count: number; active_rate: number}
  retention_analysis: {monthly: RetentionMonthlyRow[]}
}

const fmtAud = (n: number | undefined | null) => {
  const v = Number(n ?? 0)
  return `$${v.toLocaleString('en-AU', {minimumFractionDigits: 0, maximumFractionDigits: 0})}`
}

const fmtPointsAsAud = (points: number | undefined | null) => {
  const aud = Number(points ?? 0) / 100
  return `$${aud.toLocaleString('en-AU', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
}

const fmtNumber = (n: number | undefined | null) =>
  Number(n ?? 0).toLocaleString('en-AU', {maximumFractionDigits: 0})

const fmtPct = (n: number | undefined | null, suffix = '%') =>
  `${Number(n ?? 0).toFixed(1)}${suffix}`

const fmtSignedPct = (n: number | undefined | null) => {
  if (n == null) return '0%'
  const v = Number(n)
  const sign = v > 0 ? '+ ' : v < 0 ? '- ' : ''
  return `${sign}${Math.abs(v).toFixed(2)}%`
}

const InfoTip: React.FC<{id: string; text?: string; children: React.ReactElement}> = ({
  id,
  text,
  children,
}) => {
  if (!text) return children
  return (
    <OverlayTrigger
      placement='top'
      overlay={
        <Tooltip id={`bm-tip-${id}`}>
          <span style={{whiteSpace: 'pre-line'}}>{text}</span>
        </Tooltip>
      }
    >
      {children}
    </OverlayTrigger>
  )
}

const formatMonthLabel = (monthValue: string) => {
  const [y, m] = monthValue.split('-').map(Number)
  if (!y || !m) return monthValue
  return new Date(y, m - 1, 1).toLocaleString('en-AU', {month: 'long', year: 'numeric'})
}

const SectionBlock: React.FC<{
  title: string
  subtitle?: string
  children: React.ReactNode
}> = ({title, subtitle, children}) => (
  <div className='d-flex flex-column gap-5'>
    <div>
      <h2 className={bm.sectionHeading}>{title}</h2>
      {subtitle ? <p className={bm.sectionSub}>{subtitle}</p> : null}
    </div>
    {children}
  </div>
)

const METRIC_ACCENT: Record<string, string> = {
  primary: 'bg-light-primary',
  success: 'bg-light-success',
  warning: 'bg-light-warning',
  danger: 'bg-light-danger',
  info: 'bg-light-info',
}

const MetricTile: React.FC<{
  id: string
  label: string
  value: string
  tip?: string
  accent?: keyof typeof METRIC_ACCENT
}> = ({id, label, value, tip, accent = 'primary'}) => (
  <InfoTip id={id} text={tip}>
    <div className={`${bm.metricTile} ${METRIC_ACCENT[accent]}`}>
      <div className={bm.metricLabel}>{label}</div>
      <div className={bm.metricValue}>{value}</div>
    </div>
  </InfoTip>
)

const LifetimeBadge: React.FC<{tip?: string; tipId?: string}> = ({tip, tipId = 'lifetime'}) => (
  <span className={bm.lifetimeBadgeGroup}>
    <span className={bm.lifetimeBadge}>Lifetime</span>
    {tip ? (
      <InfoTip id={tipId} text={tip}>
        <span className={bm.lifetimeBadgeInfo} role='button' tabIndex={0}>
          <KTIcon iconName='information-5' className='fs-7 text-gray-500' />
        </span>
      </InfoTip>
    ) : null}
  </span>
)

const MonthlyBadge: React.FC<{short?: boolean}> = ({short}) => (
  <span className={bm.monthlyBadge}>{short ? 'Monthly' : 'Month filter'}</span>
)

const PlatformBadge: React.FC = () => <span className={bm.platformBadge}>Platform</span>

const POINTS_ICON_TONE: Record<'blue' | 'green' | 'orange' | 'purple', string> = {
  blue: bm.pointsSummaryIcon_blue,
  green: bm.pointsSummaryIcon_green,
  orange: bm.pointsSummaryIcon_orange,
  purple: bm.pointsSummaryIcon_purple,
}

const PointsSummaryMetric: React.FC<{
  label: string
  value: string
  icon: string
  tone: 'blue' | 'green' | 'orange' | 'purple'
  tip?: string
}> = ({label, value, icon, tone, tip}) => {
  const labelEl = <div className={bm.pointsSummaryLabel}>{label}</div>
  return (
    <div className={bm.pointsSummaryMetric}>
      <div className={`${bm.pointsSummaryIcon} ${POINTS_ICON_TONE[tone]}`}>
        <KTIcon iconName={icon} className='fs-2' />
      </div>
      {tip ? (
        <InfoTip id={`points-${label.replace(/\s+/g, '-').toLowerCase()}`} text={tip}>
          {labelEl}
        </InfoTip>
      ) : (
        labelEl
      )}
      <div className={bm.pointsSummaryValue}>{value}</div>
    </div>
  )
}

const RateMetricTile: React.FC<{
  label: string
  value: string
  icon: string
  tone: 'info' | 'success'
  tip?: string
}> = ({label, value, icon, tone, tip}) => (
  <InfoTip id={`rate-${label.replace(/\s+/g, '-').toLowerCase()}`} text={tip}>
    <div
      className={`${bm.rateMetricTile} ${
        tone === 'success' ? bm.rateMetricTile_success : bm.rateMetricTile_info
      }`}
    >
      <KTIcon iconName={icon} className={`fs-2 ${tone === 'success' ? 'text-success' : 'text-info'}`} />
      <div className={bm.rateMetricLabel}>{label}</div>
      <div className={`${bm.rateMetricValue} ${tone === 'success' ? 'text-success' : 'text-info'}`}>
        {value}
      </div>
    </div>
  </InfoTip>
)

const CohortRetentionTile: React.FC<{
  label: string
  value: string
  monthLabel: string
  icon: string
  tone: 'success' | 'info'
  tip?: string
}> = ({label, value, monthLabel, icon, tone, tip}) => (
  <InfoTip id={`cohort-${label.replace(/\s+/g, '-').toLowerCase()}`} text={tip}>
    <div
      className={`${bm.cohortRetentionTile} ${
        tone === 'success' ? bm.cohortRetentionTile_success : bm.cohortRetentionTile_info
      }`}
    >
      <KTIcon iconName={icon} className={`fs-2x ${tone === 'success' ? 'text-success' : 'text-info'}`} />
      <div className={bm.cohortRetentionLabel}>{label}</div>
      <div className={bm.cohortRetentionMonth}>{monthLabel}</div>
      <div className={`${bm.cohortRetentionValue} ${tone === 'success' ? 'text-success' : 'text-info'}`}>
        {value}
      </div>
    </div>
  </InfoTip>
)

const SubscriberCompareTile: React.FC<{
  label: string
  value: number
  icon: string
  tone: 'success' | 'danger'
}> = ({label, value, icon, tone}) => (
  <div
    className={`${bm.subscriberCompareTile} ${
      tone === 'success' ? bm.subscriberCompareTile_success : bm.subscriberCompareTile_danger
    }`}
  >
    <div className={bm.subscriberCompareIconWrap}>
      <KTIcon
        iconName={icon}
        className={`fs-2x ${tone === 'success' ? 'text-success' : 'text-danger'}`}
      />
    </div>
    <div className={bm.subscriberCompareLabel}>{label}</div>
    <div className={`${bm.subscriberCompareValue} ${tone === 'success' ? 'text-success' : 'text-danger'}`}>
      {fmtNumber(value)}
    </div>
  </div>
)

const ChartCard: React.FC<{
  title: string
  subtitle?: string
  tip?: string
  icon?: string
  badge?: React.ReactNode
  children: React.ReactNode
  bodyClassName?: string
  fillHeight?: boolean
}> = ({
  title,
  subtitle,
  tip,
  icon = 'chart-simple',
  badge,
  children,
  bodyClassName = 'pt-2',
  fillHeight = false,
}) => (
  <div className={`card shadow-sm ${bm.chartCard} ${fillHeight ? bm.chartCard_fill : ''}`}>
    <div className='card-header border-0 pt-5 pb-0'>
      <h3 className='card-title align-items-start flex-column m-0 w-100'>
        <span className='d-flex align-items-center justify-content-between flex-wrap gap-2 card-label fw-bold text-gray-900 w-100'>
          <span className='d-flex align-items-center gap-2'>
            <KTIcon iconName={icon} className='fs-2 text-primary' />
            {tip ? (
              <InfoTip id={`chart-${title.replace(/\s+/g, '-').toLowerCase()}`} text={tip}>
                <span>{title}</span>
              </InfoTip>
            ) : (
              <span>{title}</span>
            )}
          </span>
          {badge ? <span className='ms-auto'>{badge}</span> : null}
        </span>
        {subtitle ? (
          <span className='text-muted mt-1 fw-semibold fs-7'>{subtitle}</span>
        ) : null}
      </h3>
    </div>
    <div
      className={`card-body ${bodyClassName} ${fillHeight ? bm.chartCardBody_fill : ''}`}
    >
      {children}
    </div>
  </div>
)

const BusinessMatrixWrapper: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [data, setData] = useState<BusinessMatrixData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const month =
    formatDateToYearMonth(selectedDate) ?? formatDateToYearMonth(new Date()) ?? ''
  const monthInput = month.slice(0, 7)
  const monthLabel = formatMonthLabel(month)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await restRequest.getBusinessMatrixData(month)
        if (cancelled) return
        setData(res?.data?.data ?? null)
      } catch (e: any) {
        if (cancelled) return
        setError(e?.message ?? 'Failed to load Business Matrix data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [month])

  /* ------------------------------------------------------------ */
  /* Chart configurations                                          */
  /* ------------------------------------------------------------ */

  const revenueChart = useMemo(() => {
    const breakdown = data?.revenue.monthly_breakdown ?? []
    const months = breakdown.map((r) => r.month)
    const subs = breakdown.map((r) => Number(r.subscription ?? 0))
    const passengerCredit = breakdown.map((r) =>
      Number(
        r.passenger_credit ??
          (r as {passenger_credit_revenue?: number}).passenger_credit_revenue ??
          (r as {point_redemption?: number}).point_redemption ??
          0,
      ),
    )
    const selectedIdx = data?.revenue.selected_month_index ?? -1
    const yMax = data?.revenue.chart_y_axis_max ?? 100

    const series = [
      {name: 'Subscription Revenue', data: subs},
      {name: 'Passenger Credit Revenue', data: passengerCredit},
    ]
    const options: ApexOptions = {
      chart: {type: 'bar', stacked: true, toolbar: {show: false}, fontFamily: 'inherit'},
      plotOptions: {bar: {columnWidth: '55%', borderRadius: 3}},
      stroke: {show: false},
      dataLabels: {enabled: false},
      colors: ['#2EBF85', '#F5C24E'],
      xaxis: {categories: months, labels: {style: {fontSize: '11px'}}},
      yaxis: {
        min: 0,
        max: yMax,
        tickAmount: 5,
        forceNiceScale: false,
        labels: {
          formatter: (v: number) => (v >= 1000 ? `${Math.round(v / 100) / 10}k` : `${v}`),
          style: {fontSize: '11px'},
        },
      },
      legend: {position: 'bottom'},
      tooltip: {y: {formatter: (v: number) => fmtAud(v)}},
      grid: {strokeDashArray: 3},
      annotations:
        selectedIdx >= 0
          ? {
              xaxis: [
                {
                  x: selectedIdx - 0.45,
                  x2: selectedIdx + 0.45,
                  fillColor: '#3699FF',
                  opacity: 0.1,
                  borderWidth: 0,
                  strokeDashArray: 0,
                },
              ],
            }
          : undefined,
    }
    return {series, options}
  }, [data])

  const mauChart = useMemo(() => {
    const months = data?.engagement.mau_dau_monthly?.map((r) => r.month) ?? []
    const drivers = data?.engagement.mau_dau_monthly?.map((r) => r.mau_driver) ?? []
    const passengers = data?.engagement.mau_dau_monthly?.map((r) => r.mau_passenger) ?? []
    const series = [
      {name: 'MAU (Driver)', data: drivers},
      {name: 'MAU (Passenger)', data: passengers},
    ]
    const options: ApexOptions = {
      chart: {type: 'bar', stacked: true, toolbar: {show: false}, fontFamily: 'inherit'},
      plotOptions: {bar: {columnWidth: '55%', borderRadius: 3}},
      stroke: {show: false},
      dataLabels: {enabled: false},
      colors: ['#2EBF85', '#F5C24E'],
      xaxis: {categories: months, labels: {style: {fontSize: '11px'}}},
      yaxis: {labels: {style: {fontSize: '11px'}}},
      legend: {position: 'bottom'},
      grid: {strokeDashArray: 3},
    }
    return {series, options}
  }, [data])

  const retentionLineChart = useMemo(() => {
    const months = data?.retention_analysis.monthly?.map((r) => r.month) ?? []
    const driver = data?.retention_analysis.monthly?.map((r) => r.driver_retention) ?? []
    const passenger = data?.retention_analysis.monthly?.map((r) => r.passenger_retention) ?? []
    const series = [
      {name: 'Driver Retention', data: driver},
      {name: 'Passenger Retention', data: passenger},
    ]
    const options: ApexOptions = {
      chart: {type: 'line', toolbar: {show: false}, fontFamily: 'inherit'},
      stroke: {curve: 'smooth', width: 2.5},
      colors: ['#2EBF85', '#5A8DEE'],
      markers: {size: 4},
      dataLabels: {enabled: false},
      xaxis: {categories: months, labels: {style: {fontSize: '11px'}}},
      yaxis: {
        min: 0,
        max: 100,
        labels: {style: {fontSize: '11px'}, formatter: (v: number) => `${Math.round(v)}`},
      },
      legend: {show: false},
      grid: {strokeDashArray: 3},
      tooltip: {y: {formatter: (v: number) => `${Number(v).toFixed(1)}%`}},
    }
    return {series, options}
  }, [data])

  const activePassengerGauge = useMemo(() => {
    const value = Math.max(0, Math.min(100, data?.passengers.active_rate ?? 0))
    const options: ApexOptions = {
      chart: {type: 'radialBar', sparkline: {enabled: true}, fontFamily: 'inherit'},
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {size: '60%'},
          track: {background: '#F1F1F4', strokeWidth: '100%'},
          dataLabels: {
            name: {show: false},
            value: {
              offsetY: -2,
              fontSize: '22px',
              fontWeight: 700,
              formatter: (v: any) => `${Number(v).toFixed(1)}%`,
            },
          },
        },
      },
      stroke: {lineCap: 'round'},
      colors: ['#F5C24E'],
    }
    return {series: [value], options}
  }, [data])

  const renderHero = () => (
    <div className='card overflow-hidden shadow-sm'>
      <div className='card-body p-8 p-xl-10'>
        <div className='d-flex flex-column flex-xl-row justify-content-between align-items-xl-start gap-6 gap-xl-8'>
          <div className='flex-grow-1 min-w-0'>
            <span className='badge badge-light-primary fw-bold mb-3'>Growth &amp; retention</span>
            <h1 className='fw-bold text-gray-900 mb-2'>Business Matrix</h1>
            <div className='text-muted fs-6'>
              Revenue, loyalty, subscriber, and engagement analytics for{' '}
              <span className='text-gray-800 fw-semibold'>{monthLabel}</span>.
            </div>
          </div>

          <div className='card border border-gray-200 shadow-none flex-shrink-0 w-100 w-xl-auto min-w-xl-420px'>
            <div className='card-body p-4 p-lg-5'>
              <div className='d-flex align-items-center justify-content-between gap-3 mb-3'>
                <div className='text-gray-800 fw-bold fs-7'>Filter by month</div>
                <span className='badge badge-light-info fs-8'>{monthLabel}</span>
              </div>
              <input
                type='month'
                value={monthInput}
                onChange={(e) => {
                  const value = e.target.value
                  if (value) setSelectedDate(new Date(`${value}-01T00:00:00`))
                }}
                className='form-control form-control-sm bg-white border-primary'
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (loading && !data) {
    return (
      <div className={bm.page}>
        {renderHero()}
        <div className='card shadow-sm'>
          <div className='card-body d-flex flex-column align-items-center justify-content-center py-15'>
            <div className='spinner-border text-primary mb-4' />
            <div className='text-muted fw-semibold'>Loading business matrix…</div>
          </div>
        </div>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className={bm.page}>
        {renderHero()}
        <div className='alert alert-danger mb-0'>{error}</div>
      </div>
    )
  }

  const r = data?.revenue
  const p = data?.points
  const rates = data?.rates
  const subs = data?.subscribers
  const passengers = data?.passengers
  const selectedRetentionIdx = data?.revenue.selected_month_index ?? -1
  const selectedRetention =
    selectedRetentionIdx >= 0
      ? data?.retention_analysis.monthly?.[selectedRetentionIdx]
      : undefined

  return (
    <div className={bm.page}>
      {renderHero()}

      <SectionBlock
        title='Revenue & Loyalty Metrics'
        subtitle={`Subscription and passenger credit performance for ${monthLabel}.`}
      >
        <div className='row g-5 g-xl-6'>
          <div className={`col-xl-5 ${bm.pairCol}`}>
            <ChartCard
              title='Total Revenue & Sub-Metrics'
              subtitle={`${data?.year ?? ''} monthly trend`}
              icon='financial-schedule'
              fillHeight
              bodyClassName='pt-3 pb-5'
            >
              <ReactApexChart
                options={revenueChart.options}
                series={revenueChart.series}
                type='bar'
                height={200}
              />
              <div className='separator separator-dashed my-4' />
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0'>
                  <thead>
                    <tr className='fw-bold text-gray-600 fs-7'>
                      <th>% Change (MoM)</th>
                      <th className='text-end'>Change</th>
                    </tr>
                  </thead>
                  <tbody className='fs-7'>
                    <tr>
                      <td className='text-gray-800'>Subscription Revenue</td>
                      <td className='text-end fw-bold text-gray-900'>
                        {fmtSignedPct(r?.mom_change.subscription)}
                      </td>
                    </tr>
                    <tr>
                      <td className='text-gray-800'>Passenger Credit Revenue</td>
                      <td className='text-end fw-bold text-gray-900'>
                        {fmtSignedPct(r?.mom_change.passenger_credit)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>

          <div className={`col-xl-7 ${bm.pairCol}`}>
            <div className={bm.revenueRightStack}>
              <div className='card shadow-sm flex-grow-1'>
                <div className='card-body p-5'>
                  <div className='row g-4'>
                    <div className='col-md-4'>
                      <MetricTile
                        id='driver-sub-rev'
                        label='Driver Subscription Revenue'
                        value={fmtAud(r?.subscription)}
                        tip='Driver Subscription Rev: (Active Subs × Tier Fee)'
                        accent='success'
                      />
                    </div>
                    <div className='col-md-4'>
                      <MetricTile
                        id='passenger-credit-rev'
                        label='Passenger Credit Revenue'
                        value={fmtAud(r?.passenger_credit)}
                        tip='Passenger Credit Revenue: (Points Purchased × Point Value)'
                        accent='primary'
                      />
                    </div>
                    <div className='col-md-4'>
                      <MetricTile
                        id='churn'
                        label='Subscription Churn'
                        value={fmtPct(rates?.churn_rate)}
                        tip={rates?.churn_formula}
                        accent='danger'
                      />
                    </div>
                  </div>
                </div>
              </div>
              <ChartCard
                title='Revenue Matrix'
                icon='dollar'
                fillHeight
                bodyClassName='pt-3 pb-5'
              >
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0'>
                    <thead>
                      <tr className='fw-bold text-gray-600 fs-7'>
                        <th>Metric</th>
                        <th className='text-end'>Value</th>
                      </tr>
                    </thead>
                    <tbody className='fs-7'>
                      <tr>
                        <td className='text-gray-800'>Total Revenue</td>
                        <td className='text-end fw-bold text-gray-900'>{fmtAud(r?.total)}</td>
                      </tr>
                      <tr>
                        <td className='text-gray-800'>Driver Subscription Revenue</td>
                        <td className='text-end fw-semibold'>{fmtAud(r?.subscription)}</td>
                      </tr>
                      <tr>
                        <td className='text-gray-800'>Passenger Credit Revenue</td>
                        <td className='text-end fw-semibold'>{fmtAud(r?.passenger_credit)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </ChartCard>
            </div>
          </div>
        </div>
      </SectionBlock>

      <div className={`card shadow-sm ${bm.pointsSummaryCard}`}>
        <div className='card-header border-0 pt-5 pb-0'>
          <h3 className='card-title align-items-start flex-column m-0'>
            <span className='d-flex align-items-center flex-wrap gap-2 card-label fw-bold text-gray-900'>
              <span>Points Summary</span>
              <LifetimeBadge
                tipId='points-summary-lifetime'
                tip='Lifetime platform point totals — not affected by the month filter.'
              />
            </span>
            <span className='text-muted mt-1 fw-semibold fs-7'>All-time platform totals</span>
          </h3>
        </div>
        <div className='card-body pt-4 pb-5'>
          <div className={bm.pointsSummaryMetrics}>
            <PointsSummaryMetric
              label='Total Points Issued'
              value={fmtNumber(p?.issued)}
              icon='medal-star'
              tone='blue'
            />
            <PointsSummaryMetric
              label='Total Points Redeemed'
              value={fmtNumber(p?.redeemed)}
              icon='arrows-circle'
              tone='green'
            />
            <PointsSummaryMetric
              label='Points Not Redeemed'
              value={fmtNumber(p?.not_redeemed)}
              icon='timer'
              tone='orange'
              tip='Passenger Wallets + Driver Balances'
            />
            <PointsSummaryMetric
              label='Passenger Wallet Balance'
              value={fmtPointsAsAud(p?.in_passenger_wallets)}
              icon='wallet'
              tone='purple'
              tip='Points held in passenger wallets (100 points = $1 AUD)'
            />
          </div>
          <div className={bm.breakageLifetimeTile}>
            <div className={bm.breakageLifetimeLeft}>
              <div className='d-flex align-items-center flex-wrap gap-2'>
                <span className='fw-bold text-gray-900 fs-6'>Breakage Rate</span>
                <LifetimeBadge tipId='breakage-lifetime' tip={rates?.breakage_formula} />
              </div>
              <p className='text-muted fs-7 mb-0 mt-2'>
                Based on lifetime issued and unredeemed points.
              </p>
            </div>
            <div className={bm.breakageLifetimeValue}>{fmtPct(rates?.breakage_rate)}</div>
          </div>
        </div>
      </div>

      <SectionBlock
        title='Growth, Subscriber & Engagement'
        subtitle={`Subscriber movement, monthly rates, and active users for ${monthLabel}.`}
      >
        <div className='row g-5 g-xl-6'>
          <div className={`col-xl-5 ${bm.pairCol}`}>
            <ChartCard
              title='Driver & Passenger MAU'
              subtitle='Unique users engaged per month'
              icon='people'
              badge={<MonthlyBadge short />}
              fillHeight
              bodyClassName={`pt-3 pb-5 ${bm.mauChartBody}`}
            >
              <ReactApexChart
                options={mauChart.options}
                series={mauChart.series}
                type='bar'
                height={200}
              />
            </ChartCard>
          </div>

          <div className={`col-xl-7 ${bm.pairCol}`}>
            <ChartCard
              title='Monthly Performance'
              subtitle={monthLabel}
              icon='chart-line-up'
              badge={<MonthlyBadge short />}
              fillHeight
              bodyClassName='pt-3 pb-5'
            >
              <div className={`${bm.rateMetricGrid} ${bm.rateMetricGrid_fill}`}>
                <RateMetricTile
                  label='Retention Rate'
                  value={fmtPct(rates?.retention_rate)}
                  icon='shield-tick'
                  tone='info'
                  tip={rates?.retention_formula}
                />
                <RateMetricTile
                  label='Growth Rate'
                  value={fmtPct(rates?.growth_rate)}
                  icon='graph-up'
                  tone='success'
                  tip={rates?.growth_formula}
                />
              </div>
            </ChartCard>
          </div>
        </div>

        <div className='row g-5 g-xl-6'>
          <div className={`col-xl-7 ${bm.pairCol}`}>
            <ChartCard
              title='Subscriber Analysis'
              icon='profile-user'
              badge={<MonthlyBadge short />}
              fillHeight
              bodyClassName='pt-3 pb-5'
            >
              <div className={`${bm.subscriberTablePanel} ${bm.subscriberTablePanel_fill}`}>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 mb-0'>
                    <thead>
                      <tr className='fw-bold text-gray-600 fs-7'>
                        <th>Metric</th>
                        <th className='text-end'>Absolute</th>
                        <th className='text-end'>MoM</th>
                      </tr>
                    </thead>
                    <tbody className='fs-7'>
                      <tr>
                        <td className='text-gray-800'>Start of Month Subscribers</td>
                        <td className='text-end fw-semibold'>
                          {fmtNumber(subs?.start_of_month)}
                        </td>
                        <td
                          className={`text-end fw-semibold ${
                            (subs?.mom_change.start_of_month ?? 0) >= 0
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          {fmtSignedPct(subs?.mom_change.start_of_month)}
                        </td>
                      </tr>
                      <tr>
                        <td className='text-gray-800'>New Driver Subscriptions</td>
                        <td className='text-end fw-semibold'>
                          {fmtNumber(subs?.new_driver_subscriptions)}
                        </td>
                        <td
                          className={`text-end fw-semibold ${
                            (subs?.mom_change.new_driver_subscriptions ?? 0) >= 0
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          {fmtSignedPct(subs?.mom_change.new_driver_subscriptions)}
                        </td>
                      </tr>
                      <tr>
                        <td className='text-gray-800'>Churned Subscribers</td>
                        <td className='text-end fw-semibold'>{fmtNumber(subs?.churned)}</td>
                        <td
                          className={`text-end fw-semibold ${
                            (subs?.mom_change.churned ?? 0) <= 0 ? 'text-success' : 'text-danger'
                          }`}
                        >
                          {fmtSignedPct(subs?.mom_change.churned)}
                        </td>
                      </tr>
                      <tr>
                        <td className='text-gray-800'>End of Month Subscribers</td>
                        <td className='text-end fw-semibold'>
                          {fmtNumber(subs?.end_of_month)}
                        </td>
                        <td
                          className={`text-end fw-semibold ${
                            (subs?.mom_change.end_of_month ?? 0) >= 0
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          {fmtSignedPct(subs?.mom_change.end_of_month)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
              </div>
            </ChartCard>
          </div>

          <div className={`col-xl-5 ${bm.pairCol}`}>
            <ChartCard
              title='New vs Did Not Renew'
              icon='element-11'
              badge={<MonthlyBadge short />}
              fillHeight
              bodyClassName={`pt-3 pb-5 ${bm.subscriberCompareCardBody}`}
            >
              <div className={`${bm.subscriberCompareTiles} ${bm.subscriberCompareTiles_fill}`}>
                <SubscriberCompareTile
                  label='New Subscribers'
                  value={subs?.new_driver_subscriptions ?? 0}
                  icon='user-tick'
                  tone='success'
                />
                <SubscriberCompareTile
                  label='Did Not Renew'
                  value={subs?.churned ?? 0}
                  icon='cross-circle'
                  tone='danger'
                />
              </div>
            </ChartCard>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock
        title='Passenger & Retention Trends'
        subtitle={`Platform passenger base and cohort retention for ${monthLabel}.`}
      >
        <div className='row g-5 g-xl-6'>
          <div className={`col-xl-4 ${bm.pairCol}`}>
            <ChartCard
              title='Passenger Metrics'
              icon='user-square'
              fillHeight
              bodyClassName='pt-3 pb-5'
            >
              <div className={`${bm.passengerMetricStack} ${bm.passengerMetricStack_fill}`}>
                <div className={bm.passengerMetricCard}>
                  <div className='d-flex align-items-center justify-content-between mb-2'>
                    <span className={bm.metricLabel}>Total Registered Passengers</span>
                    <PlatformBadge />
                  </div>
                  <div className={`${bm.passengerMetricValue} text-primary`}>
                    {fmtNumber(passengers?.total_registered)}
                  </div>
                </div>
                <div className={`${bm.passengerMetricCard} ${bm.passengerMetricCard_gauge}`}>
                  <div className='d-flex align-items-center justify-content-between mb-1'>
                    <span className={bm.metricLabel}>Active Passenger Rate</span>
                    <MonthlyBadge short />
                  </div>
                  <ReactApexChart
                    options={activePassengerGauge.options}
                    series={activePassengerGauge.series}
                    type='radialBar'
                    height={130}
                  />
                </div>
              </div>
            </ChartCard>
          </div>
          <div className={`col-xl-8 ${bm.pairCol}`}>
            <ChartCard
              title='Retention Analysis'
              subtitle='Driver and passenger cohort retention'
              icon='chart-line'
              badge={<MonthlyBadge short />}
              fillHeight
              bodyClassName='pt-3 pb-5'
            >
              <ReactApexChart
                options={retentionLineChart.options}
                series={retentionLineChart.series}
                type='line'
                height={220}
              />
              <div className='separator separator-dashed my-5' />
              <div className={bm.cohortRetentionGrid}>
                <CohortRetentionTile
                  label='Driver Retention'
                  monthLabel={monthLabel}
                  value={fmtPct(selectedRetention?.driver_retention)}
                  icon='car'
                  tone='success'
                  tip={`(End − New) / Start × 100 for ${monthLabel}. Start = users active in the prior month.`}
                />
                <CohortRetentionTile
                  label='Passenger Retention'
                  monthLabel={monthLabel}
                  value={fmtPct(selectedRetention?.passenger_retention)}
                  icon='profile-user'
                  tone='info'
                  tip={`(End − New) / Start × 100 for ${monthLabel}. Start = users active in the prior month.`}
                />
              </div>
            </ChartCard>
          </div>
        </div>
      </SectionBlock>
    </div>
  )
}

export default BusinessMatrixWrapper
